<template>
  <div class="pageRoot linkListPage">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <unlock-group-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="解锁组名称"
                    prop="groupName"
                    align="center"
                    header-align="center"
                    min-width="180"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.groupName || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="关联解锁方式"
                    align="center"
                    header-align="center"
                    min-width="220"
                    class-name="categoryColumn"
                  >
                    <template slot-scope="scope">
                      <div
                        v-if="scope.row.lockVOS && scope.row.lockVOS.length"
                        class="relationCell"
                      >
                        <el-tag
                          v-for="item in getVisibleRelations(scope.row.lockVOS)"
                          :key="'lock-' + item.id"
                          size="mini"
                          effect="plain"
                          class="categoryTag relationTag"
                          :class="'relationTag--tone-' + getRelationTagTone(item.id)"
                        >
                          {{ item.name }}
                        </el-tag>
                        <el-tag
                          v-if="getHiddenRelationCount(scope.row.lockVOS) > 0"
                          size="mini"
                          effect="plain"
                          class="categoryTag"
                        >
                          +{{ getHiddenRelationCount(scope.row.lockVOS) }}
                        </el-tag>
                      </div>
                      <span v-else class="cellEmptyPill">未关联</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="解锁类型"
                    prop="lockGroupType"
                    align="center"
                    header-align="center"
                    min-width="140"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getLockTypeVariant(scope.row.lockGroupType)"
                      >
                        {{ scope.row.lockGroupType || '—' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="120"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="lookInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-lock" /></div>
                <p class="tableEmpty__title">暂无解锁组</p>
                <p class="tableEmpty__desc">调整筛选条件或新增解锁组。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="dialogOpen()"
                >
                  新增解锁组
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <unlock-group-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog
      :title="
        nowStatus === 0
          ? '新建解锁组'
          : nowStatus === 1
          ? '查看解锁组'
          : '编辑解锁组'
      "
      :visible.sync="dialogVisible"
      :before-close="handleClose"
      style="padding: 0 50px"
      width="40%"
    >
      <el-form
        ref="ruleForm"
        :model="ruleForm"
        :rules="rules"
        label-width="120px"
        size="mini"
        v-loading="dialogLoading"
        :disabled="nowStatus === 1"
      >
        <el-form-item label="解锁组名称" prop="groupName">
          <el-input
            v-model="ruleForm.groupName"
            placeholder="解锁组名称"
          ></el-input>
        </el-form-item>
        <el-form-item label="关联解锁方式" prop="connectLockType">
          <el-select
            v-model="ruleForm.connectLockType"
            multiple
            placeholder="请选择"
            style="width: 100%"
            filterable
            clearable
          >
            <el-option
              v-for="item in lockTypeOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id + ''"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="解锁类型" prop="lockGroupType">
          <el-input
            v-model="ruleForm.lockGroupType"
            placeholder="解锁类型"
          ></el-input>
        </el-form-item>
        <el-form-item label="备注" prop="message">
          <el-input
            v-model="ruleForm.message"
            type="textarea"
            :rows="2"
            placeholder="备注信息"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <div class="footerInfo" v-if="nowStatus === 1">
          <div></div>
          <div>
            <el-button @click="handleClose" size="mini">取 消</el-button>
            <el-button type="primary" @click="changeEdit()" size="mini" v-check-role>
              编 辑
            </el-button>
          </div>
        </div>
        <div v-else>
          <el-button @click="handleClose" size="mini">取 消</el-button>
          <el-button type="primary" size="mini" @click="submit">保 存</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getLockGroupList,
  getLockMode,
  getLockGroupById,
  lockGroupCreate,
  lockGroupEdit,
  getAllLockType,
} from '../../../api/unlockGroup'
import UnlockGroupListFilterPanel from './UnlockGroupListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import {
  getHiddenRelationCount,
  getLockTypeVariant,
  getRelationTagTone,
  getVisibleRelations,
} from '../unlockListHelpers'

export default {
  name: 'UnlockGroupIndex',
  components: {
    UnlockGroupListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      groupName: '',
      lockGroupType: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
      typeOptions: [],
      lockTypeOptions: [],
      dialogVisible: false,
      ruleForm: {
        groupName: '',
        lockVOS: [],
        lockGroupType: '',
        message: '',
        connectLockType: [],
      },
      rules: {
        groupName: [
          { required: true, message: '解锁组名称不能为空', trigger: 'blur' },
        ],
        connectLockType: [
          {
            required: true,
            message: '关联解锁方式不能为空',
            trigger: 'change',
          },
        ],
        lockGroupType: [
          { required: true, message: '解锁类型不能为空', trigger: 'blur' },
        ],
      },
      nowStatus: 0,
      dialogLoading: false,
      id: '',
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.groupName, this.lockGroupType].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.groupName || '').trim()) {
        chips.push({
          key: 'groupName',
          label: '组名称',
          value: this.groupName.trim(),
        })
      }
      if (String(this.lockGroupType || '').trim()) {
        chips.push({
          key: 'lockGroupType',
          label: '解锁类型',
          value: this.lockGroupType.trim(),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        groupName: this.groupName,
        lockGroupType: this.lockGroupType,
        activeFilterCount: this.activeFilterCount,
        lockGroupTypeOptions: this.typeOptions,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.dialogOpen(),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:groupName': (value) => {
          this.groupName = value
        },
        'update:lockGroupType': (value) => {
          this.lockGroupType = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getLockType()
    this.getAllLockTypeFun()
    this.getList()
  },
  methods: {
    getLockTypeVariant,
    getRelationTagTone,
    getVisibleRelations,
    getHiddenRelationCount,
    getList() {
      this.loading = true
      getLockGroupList({
        pageNum: this.currentPage,
        pageSize: this.currentPageSize,
        groupName: this.groupName,
        lockGroupType: this.lockGroupType,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.groupName = ''
      this.lockGroupType = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    getLockType() {
      getLockMode().then((res) => {
        if (res.success) {
          this.typeOptions = res.data || []
        }
      })
    },
    lookInfo(id) {
      this.id = id
      this.dialogOpen(id)
    },
    handleClose() {
      this.ruleForm = {
        groupName: '',
        lockVOS: [],
        lockGroupType: '',
        message: '',
        connectLockType: [],
      }
      this.id = ''
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.clearValidate()
      }
      this.dialogVisible = false
    },
    dialogOpen(id = '') {
      if (id) {
        this.nowStatus = 1
        this.dialogLoading = true
        getLockGroupById({ id })
          .then((res) => {
            if (res.success) {
              const data = { ...res.data }
              data.connectLockType = data.connectLockType.split(',')
              if (data.connectLockType[data.connectLockType.length - 1] === '') {
                data.connectLockType.splice(data.connectLockType.length - 1, 1)
              }
              this.ruleForm = data
            }
          })
          .finally(() => {
            this.dialogLoading = false
          })
      } else {
        this.nowStatus = 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.ruleForm) {
          this.$refs.ruleForm.clearValidate()
        }
      })
    },
    changeEdit() {
      this.nowStatus = 2
    },
    submit() {
      this.$refs.ruleForm.validate((valid) => {
        if (!valid) {
          this.$message.info('您还有表单没填完整！')
          return false
        }
        const list = {
          ...this.ruleForm,
          connectLockType: this.ruleForm.connectLockType.join(','),
        }
        if (this.nowStatus === 0) {
          lockGroupCreate(list).then((res) => {
            if (res.success) {
              this.$message.success('新建成功')
              this.getList()
              this.getLockType()
              this.handleClose()
            }
          })
          return
        }
        list.id = this.id
        lockGroupEdit(list).then((res) => {
          if (res.success) {
            this.$message.success('编辑成功')
            this.getList()
            this.getLockType()
            this.handleClose()
          }
        })
      })
    },
    getAllLockTypeFun() {
      getAllLockType().then((res) => {
        this.lockTypeOptions = res.data || []
      })
    },
  },
}
</script>

<style scoped>
.footerInfo {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
